import React from "react";
import { Modal, Box, Typography, Button, Paper } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useUpdateUserMutation } from "slices/usersApi";
import Swal from "sweetalert2";
import { useGetConcernsQuery } from "slices/wellness/concernSlice";
import { IMAGE_URL } from "network";
import { useAddServiceMutation } from "slices/wellness/doctorSlice";
import { useAddClinicServiceMutation } from "slices/wellness/clinicSlice";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  textAlign: "center",
};

function ConcernEdit({ open, data, usertype, onClose }) {
  const [update] = useAddServiceMutation();
  const [clinicUpdate] = useAddClinicServiceMutation();

  // All concerns
  const { data: concerns, isLoading } = useGetConcernsQuery({});

  // Extract already selected concern IDs from `concernLinks`
  const selectedIds = data?.concernLinks?.map((c) => c.concern?._id) || [];

  // console.log("selectedIds", selectedIds, concerns);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_id: data?._id,
      concerns: selectedIds,
    },
    validationSchema: Yup.object({
      concerns: Yup.array()
        .of(Yup.string())
        .min(1, "Select at least one concern"),
    }),

    onSubmit: async (values) => {
      try {
        // console.log("Updated values:", values);
        if (usertype == "doctor") {
          const response = await update(values).unwrap();
          if (response) {
            onClose();
          }
        } else if (usertype == "clinic") {
          const response = await clinicUpdate(values).unwrap();
          if (response) {
            onClose();
          }
        }
      } catch (error) {
        onClose();
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.data?.message || error?.message || "Server error",
        });
      }
    },
  });

  const { values, setFieldValue } = formik;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" mb={2}>
          Update Concerns
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            justifyContent="center"
            mb={3}
          >
            {!isLoading &&
              concerns?.data?.map((item) => {
                const isSelected = values.concerns.includes(item._id);
                return (
                  <Paper
                    key={item._id}
                    onClick={() => {
                      if (isSelected) {
                        setFieldValue(
                          "concerns",
                          values.concerns.filter((id) => id !== item._id)
                        );
                      } else {
                        setFieldValue("concerns", [
                          ...values.concerns,
                          item._id,
                        ]);
                      }
                    }}
                    sx={{
                      width: 120,
                      padding: 2,
                      textAlign: "center",
                      border: isSelected
                        ? "2px solid #1976d2"
                        : "1px solid #ccc",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#e3f2fd" : "#fff",
                      transition: "all 0.3s",
                    }}
                  >
                    <Box
                      component="img"
                      crossOrigin="anonymous"
                      src={`${IMAGE_URL}${item.icon}`}
                      alt={item.title}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        mb: 1,
                      }}
                    />
                    <Typography variant="subtitle2">{item.title}</Typography>
                  </Paper>
                );
              })}
          </Box>

          <Box mt={4} display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" type="submit">
              Update
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
}

export default ConcernEdit;
